'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RootCalculator() {
  const t = useTranslations("tools.rootCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Square root state
  const [squareInput, setSquareInput] = useState('');
  const [squareResult, setSquareResult] = useState<number | null>(null);
  const [squareError, setSquareError] = useState('');

  // Cube root state
  const [cubeInput, setCubeInput] = useState('');
  const [cubeResult, setCubeResult] = useState<number | null>(null);
  const [cubeError, setCubeError] = useState('');

  // General root state
  const [rootIndex, setRootIndex] = useState('');
  const [generalInput, setGeneralInput] = useState('');
  const [generalResult, setGeneralResult] = useState<number | null>(null);
  const [generalError, setGeneralError] = useState('');
  const [verification, setVerification] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatNumber = (num: number, decimals: number = 10): string => {
    if (Number.isInteger(num)) {
      return num.toString();
    }
    const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
    return rounded.toString();
  };

  const showError = (setter: (msg: string) => void, message: string) => {
    setter(message);
    setTimeout(() => setter(''), 5000);
  };

  const handleSquareRoot = (e: React.FormEvent) => {
    e.preventDefault();
    const input = parseFloat(squareInput);

    if (isNaN(input)) {
      showError(setSquareError, t('errorInvalidNumber'));
      return;
    }

    if (input < 0) {
      showError(setSquareError, t('errorNegativeSquare'));
      return;
    }

    const result = Math.sqrt(input);
    setSquareResult(result);
    setSquareError('');
  };

  const handleCubeRoot = (e: React.FormEvent) => {
    e.preventDefault();
    const input = parseFloat(cubeInput);

    if (isNaN(input)) {
      showError(setCubeError, t('errorInvalidNumber'));
      return;
    }

    const result = Math.cbrt(input);
    setCubeResult(result);
    setCubeError('');
  };

  const handleGeneralRoot = (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(rootIndex);
    const a = parseFloat(generalInput);

    if (isNaN(n) || isNaN(a)) {
      showError(setGeneralError, t('errorInvalidNumber'));
      return;
    }

    if (n < 2) {
      showError(setGeneralError, t('errorRootIndexMin'));
      return;
    }

    if (a < 0 && n % 2 === 0) {
      showError(setGeneralError, t('errorNegativeEvenRoot'));
      return;
    }

    let result: number;
    if (a < 0) {
      result = -Math.pow(Math.abs(a), 1/n);
    } else {
      result = Math.pow(a, 1/n);
    }

    const verificationValue = Math.pow(result, n);
    setGeneralResult(result);
    setVerification(`${formatNumber(result)}^${n} = ${formatNumber(verificationValue)}`);
    setGeneralError('');
  };

  const clearSquareRoot = () => {
    setSquareInput('');
    setSquareResult(null);
    setSquareError('');
  };

  const clearCubeRoot = () => {
    setCubeInput('');
    setCubeResult(null);
    setCubeError('');
  };

  const clearGeneralRoot = () => {
    setRootIndex('');
    setGeneralInput('');
    setGeneralResult(null);
    setGeneralError('');
    setVerification('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    purple: '#7c3aed',
    green: '#166534',
    lightGreen: '#f0fdf4',
    borderGreen: '#86efac',
    error: '#991b1b',
    errorBg: '#fef2f2',
    errorBorder: '#fca5a5',
    warning: '#78350f',
    warningBg: '#fef3c7',
    warningBorder: '#f59e0b',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '900px', margin: '0 auto' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const toolH2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, margin: '0 0 0.5rem 0' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorCardsStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1.5rem',
    marginBottom: '2rem',
  };

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    {
      color: colors.blue,
      margin: '0 0 1.5rem 0',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const symbolStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
  };

  const calculatorFormStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  };

  const rootDisplayStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: colors.textPrimary,
      margin: '1rem 0',
    },
    { fontSize: '1.25rem', flexWrap: 'wrap' as const },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const rootSymbolStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.blue },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const rootLineStyle: React.CSSProperties = {
    borderTop: `2px solid ${colors.textPrimary}`,
    paddingTop: '0.25rem',
    flex: 1,
    minWidth: '150px',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.125rem' },
    { padding: '0.75rem 1rem' }
  );

  const equalsStyle = getResponsiveStyle(
    { color: '#64748b', fontWeight: 600 },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.purple} 100%)`,
      color: 'white',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#f1f5f9',
      color: '#475569',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const resultsStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderRadius: '1rem',
    padding: '1.5rem',
    marginTop: '1.5rem',
    border: `2px solid ${colors.borderGreen}`,
  };

  const resultsH3Style: React.CSSProperties = {
    margin: '0 0 1rem 0',
    color: colors.green,
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const resultItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
  };

  const resultLabelStyle: React.CSSProperties = {
    color: '#475569',
    fontWeight: 500,
  };

  const resultValueStyle: React.CSSProperties = {
    color: colors.green,
    fontWeight: 700,
    fontSize: '1.25rem',
  };

  const errorMessageStyle: React.CSSProperties = {
    background: colors.errorBg,
    border: `2px solid ${colors.errorBorder}`,
    color: colors.error,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
  };

  const infoBoxStyle: React.CSSProperties = {
    background: colors.warningBg,
    borderLeft: `4px solid ${colors.warningBorder}`,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontSize: '0.9rem',
    color: colors.warning,
  };

  const formRowStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '1rem' },
    { flexWrap: 'wrap' as const },
    {},
    {}
  );

  const formGroupStyle: React.CSSProperties = {
    flex: 1,
    minWidth: '200px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#475569',
    fontWeight: 500,
    fontSize: '0.95rem',
  };

  const rootIndexStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    verticalAlign: 'super',
    color: colors.purple,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem',
  };

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const refLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.2s',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>{t('toolHeader')}</h2>
          </div>

          <div style={calculatorCardsStyle}>
            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={symbolStyle}>√</span> {t('squareRootTitle')}
              </h3>
              <form style={calculatorFormStyle} onSubmit={handleSquareRoot}>
                <div style={rootDisplayStyle}>
                  <span style={rootSymbolStyle}>√</span>
                  <div style={rootLineStyle}>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterNumber')}
                      value={squareInput}
                      onChange={(e) => setSquareInput(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <span style={equalsStyle}>=</span>
                  <span style={{ color: '#64748b' }}>?</span>
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button type="button" style={clearBtnStyle} onClick={clearSquareRoot}>{t('clearButton')}</button>
                </div>
              </form>
              {squareError && (
                <div style={errorMessageStyle}>{squareError}</div>
              )}
              {squareResult !== null && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>✓ {t('result')}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('squareRoot')}:</span>
                    <span style={resultValueStyle}>{formatNumber(squareResult)}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={symbolStyle}>∛</span> {t('cubeRootTitle')}
              </h3>
              <form style={calculatorFormStyle} onSubmit={handleCubeRoot}>
                <div style={rootDisplayStyle}>
                  <span style={rootSymbolStyle}>∛</span>
                  <div style={rootLineStyle}>
                    <input
                      type="number"
                      step="any"
                      placeholder={t('enterNumber')}
                      value={cubeInput}
                      onChange={(e) => setCubeInput(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <span style={equalsStyle}>=</span>
                  <span style={{ color: '#64748b' }}>?</span>
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button type="button" style={clearBtnStyle} onClick={clearCubeRoot}>{t('clearButton')}</button>
                </div>
              </form>
              {cubeError && (
                <div style={errorMessageStyle}>{cubeError}</div>
              )}
              {cubeResult !== null && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>✓ {t('result')}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('cubeRoot')}:</span>
                    <span style={resultValueStyle}>{formatNumber(cubeResult)}</span>
                  </div>
                </div>
              )}
            </div>

            <div style={calculatorCardStyle}>
              <h3 style={cardTitleStyle}>
                <span style={symbolStyle}>ⁿ√</span> {t('generalRootTitle')}
              </h3>
              <form style={calculatorFormStyle} onSubmit={handleGeneralRoot}>
                <div style={formRowStyle}>
                  <div style={{ ...formGroupStyle, maxWidth: '150px' }}>
                    <label htmlFor="root-index" style={labelStyle}>{t('rootIndexLabel')}:</label>
                    <input
                      type="number"
                      id="root-index"
                      min="2"
                      step="1"
                      placeholder={t('rootIndexPlaceholder')}
                      value={rootIndex}
                      onChange={(e) => setRootIndex(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label htmlFor="general-input" style={labelStyle}>{t('numberLabel')}:</label>
                    <input
                      type="number"
                      id="general-input"
                      step="any"
                      placeholder={t('enterNumber')}
                      value={generalInput}
                      onChange={(e) => setGeneralInput(e.target.value)}
                      style={inputStyle}
                      required
                    />
                  </div>
                </div>
                <div style={rootDisplayStyle}>
                  <span style={rootIndexStyle}>{rootIndex || 'n'}</span>
                  <span style={rootSymbolStyle}>√</span>
                  <div style={{ ...rootLineStyle, padding: '0.5rem' }}>
                    <span style={{ color: '#64748b' }}>{generalInput || t('numberPlaceholder')}</span>
                  </div>
                  <span style={equalsStyle}>=</span>
                  <span style={{ color: '#64748b' }}>?</span>
                </div>
                <div style={infoBoxStyle}>
                  💡 {t('generalRootInfo')}
                </div>
                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                  <button type="button" style={clearBtnStyle} onClick={clearGeneralRoot}>{t('clearButton')}</button>
                </div>
              </form>
              {generalError && (
                <div style={errorMessageStyle}>{generalError}</div>
              )}
              {generalResult !== null && (
                <div style={resultsStyle}>
                  <h3 style={resultsH3Style}>✓ {t('result')}</h3>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{rootIndex}{t('thRoot')}:</span>
                    <span style={resultValueStyle}>{formatNumber(generalResult)}</span>
                  </div>
                  <div style={{ ...resultItemStyle, marginBottom: 0 }}>
                    <span style={resultLabelStyle}>{t('verification')}:</span>
                    <span style={{ ...resultValueStyle, fontSize: '1rem' }}>{verification}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <h3 style={h3Style}>{t('squareRootSteps')}</h3>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('squareStep1')}</li>
          <li style={listItemStyle}>{t('squareStep2')}</li>
          <li style={listItemStyle}>{t('squareStep3')}</li>
        </ol>

        <h3 style={h3Style}>{t('cubeRootSteps')}</h3>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('cubeStep1')}</li>
          <li style={listItemStyle}>{t('cubeStep2')}</li>
          <li style={listItemStyle}>{t('cubeStep3')}</li>
        </ol>

        <h3 style={h3Style}>{t('generalRootSteps')}</h3>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('generalStep1')}</li>
          <li style={listItemStyle}>{t('generalStep2')}</li>
          <li style={listItemStyle}>{t('generalStep3')}</li>
          <li style={listItemStyle}>{t('generalStep4')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('squareRootDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('squareRootDetails1')}</p>
        <p style={paragraphStyle}>{t('squareRootDetails2')}</p>

        <h3 style={h3Style}>{t('cubeRootDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('cubeRootDetails1')}</p>
        <p style={paragraphStyle}>{t('cubeRootDetails2')}</p>

        <h3 style={h3Style}>{t('nthRootDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('nthRootDetails1')}</p>
        <p style={paragraphStyle}>{t('nthRootDetails2')}</p>

        <h3 style={h3Style}>{t('practicalApplicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><span style={strongStyle}>{t('mathTitle')}:</span> {t('mathApplication')}</li>
          <li style={listItemStyle}><span style={strongStyle}>{t('engineeringTitle')}:</span> {t('engineeringApplication')}</li>
          <li style={listItemStyle}><span style={strongStyle}>{t('physicsTitle')}:</span> {t('physicsApplication')}</li>
          <li style={listItemStyle}><span style={strongStyle}>{t('financeTitle')}:</span> {t('financeApplication')}</li>
          <li style={listItemStyle}><span style={strongStyle}>{t('statisticsTitle')}:</span> {t('statisticsApplication')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={h3Style}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={h3Style}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={h3Style}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={h3Style}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={h3Style}>{t('faq6Question')}</h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://www.cuemath.com/algebra/squares-and-square-roots/"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.calculator.net/root-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://en.wikipedia.org/wiki/Square_root"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://math.libretexts.org/Bookshelves/Applied_Mathematics/Developmental_Math_(NROC)/16:_Radical_Expressions_and_Quadratic_Equations/16.01:_Introduction_to_Roots_and_Rational_Exponents/16.1.01:_Roots"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference4')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}